import { css } from '@emotion/react';
import { BoundType, PointOfSale } from '@flysas-tech/book-value-stream-models';
import { tokens, Radio, Paragraph, Heading } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { useEffect, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import { AccrualPoints, ShortRule, formatPoints } from 'shared';

const styles = {
  slide: css`
    border-radius: ${tokens.dimension['space-4']};
    box-shadow: ${tokens.shadow.medium};
    height: 400px;
  `,
  productCardHeader: (color: string) => css`
    display: flex;
    padding: ${tokens.dimension['space-6']}${tokens.dimension['space-5']};
    background-color: ${color};
    border-radius: 16px 16px 0 0;
    flex-direction: column;
  `,
  container: css`
    display: flex;
  `,
  accrualWrapper: css`
    width: 100%;
    position: relative;
  `,
  accrualContainer: css`
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    width: 100%;
    height: 20px;
    position: relative;
    margin-top: ${tokens.dimension['space-2']};
  `,
  namePriceWrapper: css`
    display: flex;
    cursor: pointer;
    flex-direction: column;
  `,
  cabinInfo: css`
    display: flex;
    margin: 0;
  `,
  cabinPrefix: css`
    text-wrap: nowrap;
    text-transform: uppercase;
  `,
  cabinName: css`
    margin-left: ${tokens.dimension['space-2']};
    text-wrap: nowrap;
    text-transform: capitalize;
  `,
  productName: css`
    margin-left: ${tokens.dimension['space-2']};
    text-transform: capitalize;
  `,
  price: (color: string) => css`
    font-size: ${tokens.fontSize.heading['title-5']};
    font-weight: 700;
    line-height: 40px;
    color: ${color};
    margin: 0;
  `,
  oldPrice: (color: string) => css`
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    text-wrap: nowrap;
    color: ${color};
    text-decoration: line-through;
  `,
  accrualText: (color: string) => css`
    font-size: ${tokens.fontSize.paragraph['paragraph-1']};
    text-wrap: nowrap;
    color: ${color};
  `,
  radio: (color: string) => css`
    padding-block: 0;
    margin-left: -4px;
    input[type='radio']:checked::after {
      background-color: ${color};
    }
    input[type='radio'] {
      border: 2px solid ${color};
    }
    input[type='radio']:focus-visible {
      outline: 1px solid ${color};
      outline-offset: 1px;
    }
    &:first-of-type:focus-within {
      border: 0 !important;
      margin: 2px 2px 2px -4px !important;
    }
  `,
  textColor: (color: string) => css`
    color: ${color};
  `,
};

export type ProductCard = {
  id: string;
  price: number;
  cabinPrefix?: string;
  cabinName: string;
  productName: string;
  formattedPrice: string;
  originalFormattedTotalPrice?: string;
  availableSeats: number;
  longRules: string[];
  shortRules: ShortRule[];
  isPopularChoice?: boolean;
  colors: {
    backgroundColor: string;
    textColor: string;
    iconColor: string;
  };
};

type Props = {
  card: ProductCard;
  handleProductClick: (selectedId: string) => void;
  productId: string;
  boundType: BoundType;
  price: number;
  currency: string;
  isSelected: boolean;
  labels?: string[];
  accrual?: {
    data?: AccrualPoints;
    isLoading: boolean;
  };
  pointOfSale: PointOfSale;
};

export const ProductCardHeader = ({
  card,
  currency,
  isSelected,
  handleProductClick,
  productId,
  boundType,
  labels = [],
  accrual,
  pointOfSale,
}: Props) => {
  const { productName, cabinName, formattedPrice, originalFormattedTotalPrice, colors, price } = card;
  const radioButtonRef = useRef<HTMLInputElement>(null);

  const screenReaderLegend = `${cabinName} ${productName} ${price} ${currency} ${t('a11y.perAdult', {
    defaultValue: 'per adult',
  })}.${labels.length ? ' ' + labels.join(', ') : ''}`;

  useEffect(() => {
    if (isSelected) {
      // Move focus to selected radio button to ensure accessibility
      radioButtonRef.current?.focus({ preventScroll: true });
    }
  }, [isSelected]);

  return (
    <div css={styles.productCardHeader(colors.backgroundColor)}>
      <div css={styles.container}>
        <Radio
          ref={radioButtonRef}
          label={''}
          size="small"
          aria-label={screenReaderLegend}
          aria-selected={isSelected}
          name={`flight-${boundType}`}
          checked={isSelected}
          id={`card-label-${cabinName}-${productName}`}
          onChange={() => handleProductClick(productId)}
          css={styles.radio(colors.iconColor)}
        />
        <label htmlFor={`card-label-${cabinName}-${productName}`} css={styles.namePriceWrapper}>
          <div aria-hidden="true" css={styles.cabinInfo}>
            {
              <Heading as="h3" css={[styles.productName, styles.textColor(colors.textColor)]} size={'title-3'}>
                {productName}
              </Heading>
            }
          </div>
        </label>
      </div>
      {originalFormattedTotalPrice && (
        <Paragraph aria-hidden="true" css={styles.oldPrice(colors.textColor)} color="inherit" size={'paragraph-1'}>
          {originalFormattedTotalPrice}
        </Paragraph>
      )}
      <Paragraph aria-hidden="true" css={styles.price(colors.textColor)} color="inherit" size={'paragraph-4'}>
        {formattedPrice}
      </Paragraph>
      {accrual && <AccrualPointsComponent {...accrual} pointOfSale={pointOfSale} textColor={colors.textColor} />}
    </div>
  );
};

function AccrualPointsComponent({
  data,
  isLoading,
  pointOfSale,
  textColor,
}: {
  data?: AccrualPoints;
  isLoading: boolean;
  pointOfSale: PointOfSale;
  textColor: string;
}) {
  if (isLoading)
    return (
      <div css={styles.accrualWrapper}>
        <div css={styles.accrualContainer}>
          <div style={{ width: '100%' }}>
            <Skeleton width="85%" height={10} />
          </div>
        </div>
      </div>
    );

  if (!data) return null;

  return (
    <Paragraph aria-hidden="true" css={styles.accrualText(textColor)} color="inherit" size={'paragraph-1'}>
      {t('earn.accrualPoints', {
        replace: {
          bonusPoints: formatPoints(data.bonusPoints, pointOfSale as string, false),
          levelPoints: formatPoints(data.levelPoints, pointOfSale as string, false),
        },
        defaultValue: 'Earn {{bonusPoints}} bonus- & {{levelPoints}} level points',
      })}
    </Paragraph>
  );
}
